"use client";

import { useSyncExternalStore } from "react";

/**
 * Tracks whether the tab/page is currently visible to the user.
 *
 * Returns true when the tab is foregrounded and false when backgrounded.
 * SSR-safe: returns true on the server and during initial client render.
 *
 * Subscribes to three events because one isn't enough:
 * - `visibilitychange` — fires on tab switch / window minimize
 * - `pageshow` / `pagehide` — fires on Safari bfcache freeze/restore,
 *   which does NOT always fire visibilitychange
 *
 * Note: these events do NOT fire when the browser window is simply
 * covered by another window without losing focus. That's a platform
 * limitation, not a bug in this hook.
 *
 * Implementation uses `useSyncExternalStore` rather than useState+useEffect
 * so (a) React handles subscribe/unsubscribe, (b) there's no sync setState
 * in an effect body, and (c) server snapshot is explicit.
 */
function subscribe(onStoreChange: () => void): () => void {
  document.addEventListener("visibilitychange", onStoreChange);
  window.addEventListener("pageshow", onStoreChange);
  window.addEventListener("pagehide", onStoreChange);
  return () => {
    document.removeEventListener("visibilitychange", onStoreChange);
    window.removeEventListener("pageshow", onStoreChange);
    window.removeEventListener("pagehide", onStoreChange);
  };
}

function getSnapshot(): boolean {
  return document.visibilityState === "visible";
}

function getServerSnapshot(): boolean {
  return true;
}

export function usePageVisibility(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
